import {
	keepPreviousData,
	skipToken,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import type { CreateLocationRequest, LocationRequestId, VenueId } from "../../schemas/venue";
import { venueApi } from "../../venue-api";
import { queryKeys } from "../query-keys";
import { flattenPages, nextPageParam } from "./pagination";

// Location-link requests (ADR-0054): the owner asks, a platform admin decides.

const keys = queryKeys.venuePortal;
const adminKey = [...keys.all(), "admin", "location-requests"] as const;

/** Admin inbox page size (server max is 100). */
export const ADMIN_LOCATION_REQUESTS_PAGE_SIZE = 50;

/** Shortest query worth sending to the location search. */
export const LOCATION_SEARCH_MIN_CHARS = 2;

/** The venue's latest request, or null when it never asked. */
export function useLatestLocationRequest(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId
			? async () => (await venueApi.locationRequests.list(venueId)).items[0] ?? null
			: skipToken,
		queryKey: keys.locationRequests(venueId ?? ""),
	});
}

export function useRequestLocationLink(venueId: VenueId) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateLocationRequest) => venueApi.locationRequests.create(venueId, body),
		onSuccess: () => qc.invalidateQueries({ queryKey: keys.locationRequests(venueId) }),
	});
}

export function useLocationSearch(query: string) {
	const q = query.trim();
	return useQuery({
		placeholderData: keepPreviousData,
		queryFn: q.length >= LOCATION_SEARCH_MIN_CHARS ? () => venueApi.locations.search(q) : skipToken,
		queryKey: [...keys.all(), "location-search", q],
	});
}

/** The admin inbox, pending first, one keyset page at a time (ADR-0056). */
export function useAdminLocationRequests() {
	const query = useInfiniteQuery({
		getNextPageParam: nextPageParam,
		initialPageParam: undefined as string | undefined,
		queryFn: ({ pageParam }) =>
			venueApi.admin.locationRequests.list({
				cursor: pageParam,
				limit: ADMIN_LOCATION_REQUESTS_PAGE_SIZE,
			}),
		queryKey: adminKey,
	});
	return { ...query, requests: flattenPages(query.data?.pages) };
}

export type LocationRequestDecision =
	| { kind: "approve"; requestId: LocationRequestId }
	| { kind: "reject"; reason: string; requestId: LocationRequestId };

export function useDecideLocationRequest() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (decision: LocationRequestDecision) => {
			switch (decision.kind) {
				case "approve":
					return venueApi.admin.locationRequests.approve(decision.requestId);
				case "reject":
					return venueApi.admin.locationRequests.reject(decision.requestId, decision.reason);
			}
		},
		// Approval links a venue: the venue list changes too.
		onSuccess: () => qc.invalidateQueries({ queryKey: keys.all() }),
	});
}
